import { DataTable, Card, Skeleton, MetricCard, MetricCardGrid, METRIC_READ_CAPTIONS, AreaChart, MetricGauge, Alert, Button, type DataTableColumn } from '@aaimplatform/ui';
import { useRiskDashboard } from '../../hooks/useRiskDashboard';
import type { RiskSummaryResponse } from '../../api/services/risk-dashboard';
import type { components as AdminPortalUiComponents } from '@aaimplatform/institutional-types/generated/admin-portal-ui';
import { formatCalendarDate, formatPercent, formatUsdCompact, labelFor } from '../monitoring/display-format';
import { loanTypeLabel } from '../../api/loan-labels';
import { DashboardSectionLabel, GaugeUnavailable } from './dashboard-chrome';

// The page carried a search box and an Add Filter menu (risk class, sector,
// analysis period) whose state nothing read: the summary read takes no filter
// and no table was filtered on the client. The sector options (Technology,
// Healthcare, ...) were a literal list the data does not carry. They were
// removed rather than left as controls that change nothing.

// ---------------------------------------------------------------------------
// Read-outcome vocabulary -- identical to CollateralMonitoring.tsx and
// ComplianceStatus.tsx. Three dashboards answering the same question must not
// answer it in three different words.
// ---------------------------------------------------------------------------

// The withheld-read captions are the kit's METRIC_READ_CAPTIONS (GH #2917
// deliverable 5); the local names keep this page's call sites readable.
/** The read ran and rejected. */
const READ_FAILED_CAPTION = METRIC_READ_CAPTIONS.failed;
/**
 * The read has not run. Distinct from READ_FAILED_CAPTION, which states that a
 * read ran and rejected, and distinct from any caption describing a portfolio:
 * there is no portfolio here, only an absent measurement.
 */
const NOT_READ_CAPTION = METRIC_READ_CAPTIONS.unread;
/**
 * The read succeeded and did not report this figure: an omitted field, or a
 * list no engine produces, which RiskSummaryResponse sends empty.
 */
const NOT_REPORTED_CAPTION = METRIC_READ_CAPTIONS.notReported;
const READ_FAILED_HEADLINE = 'Risk dashboard data could not be read';
const REFRESH_FAILED_HEADLINE = 'Risk dashboard refresh failed';

/**
 * RiskGauge.label of the one gauge this page draws, as the service sends it.
 * The gauge renders through a literal MetricGauge title, which
 * gauge-consistency.test.ts reads from source.
 */
const LARGEST_PACKAGE_SHARE_LABEL = 'Largest Package Share';

/** TopHolding.rating is omitted when no rating is recorded for the holding. */
const NOT_RATED_CAPTION = 'Not rated';
/** StressTestScenario.probability is omitted when no engine estimates it. */
const NOT_ESTIMATED_CAPTION = 'Not estimated';

/**
 * A trend series with one point is a reading, never a trend: an area chart of
 * one point claims a history. The reading itself is the series' only point.
 */
function singleReadingMessage(subject: string, date: string | undefined): string {
  return `${subject} has one reading${date ? `, dated ${formatCalendarDate(date)}` : ''}. No earlier readings are stored, so there is no trend to draw.`;
}

/** The local clock time of a read, for the refresh-failed notice. */
function readTime(dataUpdatedAt: number): string {
  return new Date(dataUpdatedAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * The four read outcomes, QueryReadOutcome in
 * schema-registry/admin-portal-ui.openapi.yaml.
 */
type ReadOutcome = AdminPortalUiComponents['schemas']['QueryReadOutcome'];

/**
 * The read outcome the whole page renders from, derived from the fields the
 * query result already carries. The hook is a plain `useQuery` and is unchanged.
 *
 * FOUR outcomes, not two. The page previously gated on `error` and `isLoading`
 * alone and then rendered `data?.X ?? <fallback>`. TanStack v5 defines
 * `isLoading` as `isPending && isFetching`, so a FIRST fetch that is PAUSED --
 * the browser offline before this page mounted, networkMode default -- is
 * pending with isFetching false: past the skeleton branch, and neither error
 * nor success. The page fell through to its metric grid in that shape and
 * published `${data?.kpis.totalLoans ?? 0} total loans`, a count of a portfolio
 * nobody read, on an FI operator surface. A count is as much a measurement as a
 * dollar figure.
 *
 * `read` is a payload in hand. `failed` is an error with NO payload: a first
 * read that rejected. A rejected background refetch over a prior successful
 * read keeps that payload, and the page keeps showing it as `read` with a
 * refresh-failed notice stating when it was read (GH #2917 deliverable 4,
 * operator ruling 2026-09-23: the rows were measured; a failed refresh makes
 * them older, which is the staleness class of GH #2383).
 */
function readOutcome(query: {
  isError: boolean;
  isLoading: boolean;
  data: RiskSummaryResponse | undefined;
}): ReadOutcome {
  if (query.isError && query.data === undefined) return 'failed';
  if (query.isLoading) return 'loading';
  if (query.data === undefined) return 'unread';
  return 'read';
}

/**
 * The body text a section renders when it has no rows to draw.
 *
 * `emptyMessage` is what a successful read means by an empty list, and it is
 * reachable only from the `read` outcome. For a list no engine produces the
 * contract sends it empty and the message is 'Not reported'; for a list the
 * service measured, it is a measured sentence. Printing either with no read
 * issued, or with the read rejected, renders an outage as a portfolio, which
 * is strictly worse for an FI operator than an error they can see.
 */
function sectionMessage(
  outcome: ReadOutcome,
  subject: string,
  emptyMessage: string,
  rows: readonly unknown[] | undefined,
  plural = false,
): string {
  if (outcome === 'failed') return `${subject} could not be read.`;
  if (outcome !== 'read') return `${subject} not yet read.`;
  if (rows === undefined) return `${subject} ${plural ? 'were' : 'was'} not in the response.`;
  return emptyMessage;
}

/**
 * A list as the body carried it, or undefined when the body carried none.
 *
 * The same helper as CollateralMonitoring.tsx and ComplianceStatus.tsx.
 * RiskSummaryResponse requires every list, so a body without one does not
 * conform; the section then says the list was not in the response rather than
 * printing a sentence about an empty one (UI run r3, fi-g4-1).
 */
function listOrAbsent<T>(value: T[] | null | undefined): T[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

/**
 * RiskDashboard - portfolio risk summary for an FI tenant.
 *
 * Renders GET /admin/risk/summary (RiskSummaryResponse, served by
 * platform-admin-api risk-dashboard.service.ts) as the declared lists: the KPI
 * tiles, the Largest Package Share gauge, the VaR trend, the stress-test
 * table, the largest holdings and the loan type concentration. A list no
 * engine produces arrives empty and reads 'Not reported'; an omitted field
 * reads as its own absence ('Not reported', 'Not rated', 'Not estimated').
 *
 * The page states no method for any figure. The tiles used to caption VaR as
 * "historical method" / "parametric method" and default probability as an "ML
 * neural network"; the service computes none of those. Removed with the
 * contract reshape (fi-06): the VaR 95, VaR 99, average default probability
 * and critical-positions tiles; the liquidity-score, risk-weighted-exposure
 * and overall-risk-score gauges and their hardcoded bands; the
 * default-probability trend chart; the stress-test chart; the risk-class
 * donut; the holdings share and risk-class columns; and the dollar-exposure
 * and risk-rating columns of the concentration table.
 */
export function RiskDashboard() {
  const summaryQuery = useRiskDashboard();

  const outcome = readOutcome(summaryQuery);

  // `summary` is null until a read SUCCEEDS -- never a `?? 0` stand-in. After
  // one succeeds it stays through a rejected refetch, under the notice below.
  const summary: RiskSummaryResponse | null =
    outcome === 'read' ? (summaryQuery.data ?? null) : null;
  const refreshFailed = summaryQuery.isError && summaryQuery.data !== undefined;

  const failureDetail =
    summaryQuery.error instanceof Error
      ? summaryQuery.error.message
      : READ_FAILED_HEADLINE;

  // Non-null exactly when no figure may be printed, whatever the section.
  const withheldCaption: string | null =
    outcome === 'failed' ? READ_FAILED_CAPTION : summary ? null : NOT_READ_CAPTION;

  // Rows are the read's OUTPUT, so they exist only where the read succeeded.
  // That keeps every tile, chart, table and measured-empty sentence
  // unreachable on the unread and failed paths -- including the
  // refetch-rejected path, where rows from a PRIOR successful read would
  // otherwise sit under a caption saying the read failed.
  const kpiRows = summary ? listOrAbsent(summary.kpis) : undefined;
  const gaugeRows = summary ? listOrAbsent(summary.gauges) : undefined;
  const varTrendRows = summary ? listOrAbsent(summary.varTrend) : undefined;
  const stressTestRows = summary ? listOrAbsent(summary.stressTests) : undefined;
  const topHoldingRows = summary ? listOrAbsent(summary.topHoldings) : undefined;
  const loanTypeRows = summary ? listOrAbsent(summary.loanTypeConcentration) : undefined;
  const kpis = kpiRows ?? [];
  const varTrend = varTrendRows ?? [];
  const stressTests = stressTestRows ?? [];
  const topHoldings = topHoldingRows ?? [];
  const loanTypeConcentration = loanTypeRows ?? [];

  // The gauge the page draws, when the read sent it. The service sends none
  // over a book with no valued collateral: a share of nothing is not 0%.
  const largestPackageShare = gaugeRows?.find(
    (gauge) => gauge.label === LARGEST_PACKAGE_SHARE_LABEL,
  );

  // The scenario count is a MEASURED count of the table directly beneath it, so
  // it is printable only when that table has rows. An empty list is the
  // contract's word for a list no engine produces, which counts nothing. Until
  // 2026-09-21 the heading carried a hardcoded scenario count (twelve) on every
  // path, a figure no response produced (GH #2917).
  const stressTestHeading =
    stressTests.length === 0
      ? 'Stress Test Results'
      : `Stress Test Results (${stressTests.length} ${
          stressTests.length === 1 ? 'Scenario' : 'Scenarios'
        })`;

  // A cell the contract lets the service omit prints the absence in words,
  // in the tertiary color, never a 0 or a dash that reads as a figure.
  const absentCell = (caption: string) => <span className="text-tertiary">{caption}</span>;

  const stressTestColumns: DataTableColumn<RiskSummaryResponse['stressTests'][number]>[] = [
    { header: 'Scenario', accessor: 'scenario', align: 'left' },
    // The loss prints plain. It was drawn in the accent red below -30% and the
    // warning color below -20%, bands no arbiter supplies.
    {
      header: 'Portfolio Loss %',
      accessor: 'portfolioLoss',
      align: 'right',
      monospace: true,
      cell: (value) => formatPercent(Number(value)),
    },
    {
      header: 'Probability',
      accessor: 'probability',
      align: 'right',
      monospace: true,
      cell: (value) => (value == null ? absentCell(NOT_ESTIMATED_CAPTION) : formatPercent(Number(value))),
    },
  ];

  const topHoldingsColumns: DataTableColumn<RiskSummaryResponse['topHoldings'][number]>[] = [
    { header: 'Holding', accessor: 'name', align: 'left' },
    {
      header: 'Market Value',
      accessor: 'exposure',
      align: 'right',
      monospace: true,
      // The service omits the exposure of a package never valued: its stored
      // 0 is the column default, not a valuation, and it printed as '$0'
      // (ruling money-path-invented-zeros).
      cell: (value) => (value == null ? absentCell(NOT_REPORTED_CAPTION) : formatUsdCompact(Number(value))),
    },
    {
      header: 'LTV',
      accessor: 'ltv',
      align: 'right',
      monospace: true,
      // A package with no stored ratio has no LTV, which is not an LTV of 0.
      cell: (value) => (value == null ? absentCell(NOT_REPORTED_CAPTION) : formatPercent(Number(value))),
    },
    {
      header: 'Rating',
      accessor: 'rating',
      align: 'left',
      cell: (value) => (value == null ? absentCell(NOT_RATED_CAPTION) : String(value)),
    },
  ];

  const loanTypeColumns: DataTableColumn<RiskSummaryResponse['loanTypeConcentration'][number]>[] = [
    // The service groups the outstanding balance by Loan.loanType and sends the
    // code, so it is labeled here rather than printed as SCREAMING_CASE.
    {
      header: 'Loan type',
      accessor: 'loanType',
      align: 'left',
      cell: (value) => labelFor(loanTypeLabel(String(value))),
    },
    // The share prints plain: no arbiter supplies a band for it (UI run r3,
    // fi-g4-3).
    {
      header: 'Share of outstanding balance',
      accessor: 'percentage',
      align: 'right',
      monospace: true,
      cell: (value) => formatPercent(Number(value)),
    },
  ];

  if (outcome === 'loading') {
    return (
      <div>
        <MetricCardGrid className="mb-8">
          <Skeleton className="h-24 rounded-sm" />
        </MetricCardGrid>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Skeleton className="h-32 rounded-sm" />
        </div>
        <Skeleton className="h-64 rounded-sm mb-8" />
      </div>
    );
  }

  return (
    <div>
      {/* Page Heading */}
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <p className="text-sm font-sans text-tertiary">Key risk metrics, collateral concentration, VaR and stress testing, top holdings and loan type concentration</p>
      </div>

      {/*
        The notice half of the withheld-read contract. A failed read used to
        replace this whole page with a bare red callout; every section reading
        a bare dash with nothing said would be the opposite defect, since the
        operator sees withheld figures and no reason to distinguish them from a
        book that is genuinely empty.
      */}
      {refreshFailed && (
        <div className="mb-6" role="status">
          <Alert type="warning" title={REFRESH_FAILED_HEADLINE}>
            <div className="space-y-2">
              <div className="text-xs">
                {`The figures below are from the read at ${readTime(summaryQuery.dataUpdatedAt)}. The latest refresh failed: ${failureDetail}`}
              </div>
              <Button
                variant="tertiary"
                size="small"
                onClick={() => {
                  void summaryQuery.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {outcome === 'failed' && (
        <div className="mb-6" role="alert">
          <Alert type="error" title={READ_FAILED_HEADLINE}>
            <div className="space-y-2">
              <div className="text-xs">{failureDetail}</div>
              <div className="text-xs">
                The figures below are withheld, not zero. Nothing here states
                your portfolio&apos;s risk position -- the read itself failed.
              </div>
              <Button
                variant="tertiary"
                size="small"
                onClick={() => {
                  void summaryQuery.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {/* Key Risk Metrics -- one tile per RiskKpi. RiskKpi.positive is present
          only when the note states a direction; an absent positive is a note
          stating a basis or an absence and draws neutral. The note stays in
          MetricCard's monospace change slot (the Dashboard.tsx treatment,
          ruling kpi-absence-notes (a)). */}
      <DashboardSectionLabel>Key Risk Metrics</DashboardSectionLabel>
      {kpis.length > 0 ? (
        <MetricCardGrid className="mb-8">
          {kpis.map((kpi) => (
            <MetricCard
              key={kpi.label}
              label={kpi.label}
              value={kpi.value}
              change={kpi.change}
              changeType={
                kpi.positive === true ? 'positive' : kpi.positive === false ? 'negative' : 'neutral'
              }
            />
          ))}
        </MetricCardGrid>
      ) : (
        <Card className="mb-8">
          <div className="py-12 text-center text-tertiary">
            {sectionMessage(outcome, 'Key risk metrics', NOT_REPORTED_CAPTION, kpiRows, true)}
          </div>
        </Card>
      )}

      {/* Risk Health Gauges. The one gauge is null-gated: a gauge the read did
          not send renders the withheld or not-reported shell, never a
          MetricGauge fed `?? 0`, which would compute a share of 0 and read as
          a measured one. It is the largest collateral package's share of total
          collateral value, the one definition in collateral-monitoring.service.ts
          that the Health Overview gauge also reads. It carries no thresholds
          there or here: no contract field or FI policy supplies a band for it,
          and the service sends no RiskGauge.status, so it draws neutral. */}
      <DashboardSectionLabel>Risk Health Gauges</DashboardSectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {largestPackageShare ? (
          <MetricGauge
            title="Largest Package Share"
            value={largestPackageShare.value}
            max={largestPackageShare.max}
            unit={largestPackageShare.unit}
          />
        ) : (
          <GaugeUnavailable title="Largest Package Share">
            {withheldCaption ?? NOT_REPORTED_CAPTION}
          </GaugeUnavailable>
        )}
      </div>

      {/* VaR trend. A chart drawn from an empty list is a drawn claim about a
          book -- an empty plot with real axes reads as a measurement of
          nothing. No engine produces VaR history, so the service sends the list
          empty and the section reads 'Not reported'. */}
      <DashboardSectionLabel>Portfolio VaR Trend</DashboardSectionLabel>
      <div className="mb-8">
        {varTrend.length >= 2 ? (
          <AreaChart
            title="Portfolio VaR (99%)"
            data={varTrend}
            dataKey="var99"
            xAxisKey="date"
            color="rgb(var(--color-accent))"
            height={250}
          />
        ) : (
          <Card>
            <div className="py-12 text-center text-tertiary">
              {varTrend.length === 1
                ? singleReadingMessage('Portfolio VaR', varTrend[0]?.date)
                : sectionMessage(outcome, 'Portfolio VaR trend data', NOT_REPORTED_CAPTION, varTrendRows)}
            </div>
          </Card>
        )}
      </div>

      {/* Stress Test Results. No engine runs scenarios, so the service sends
          the list empty and the section reads 'Not reported'. */}
      <DashboardSectionLabel>{stressTestHeading}</DashboardSectionLabel>
      <Card className="mb-8">
        {stressTests.length > 0 ? (
          <DataTable
            columns={stressTestColumns}
            data={stressTests}
            striped
            hoverable
          />
        ) : (
          <div className="py-12 text-center text-tertiary">
            {sectionMessage(outcome, 'Stress test results', NOT_REPORTED_CAPTION, stressTestRows, true)}
          </div>
        )}
      </Card>

      {/* Top Holdings - the largest collateral packages by value. */}
      <DashboardSectionLabel>Top Holdings (Concentration Risk)</DashboardSectionLabel>
      <Card className="mb-8">
        {topHoldings.length > 0 ? (
          <DataTable
            columns={topHoldingsColumns}
            data={topHoldings}
            striped
            hoverable
          />
        ) : (
          <div className="py-12 text-center text-tertiary">
            {sectionMessage(outcome, 'Top holdings', 'No top holdings available', topHoldingRows, true)}
          </div>
        )}
      </Card>

      {/* Loan Type Concentration -- the outstanding balance by loan type
          (ruling risk-sector-relabel). The contract sends the list empty when
          nothing is outstanding, and the section says so. */}
      <DashboardSectionLabel>Loan Type Concentration</DashboardSectionLabel>
      <Card>
        {loanTypeConcentration.length > 0 ? (
          <DataTable
            columns={loanTypeColumns}
            data={loanTypeConcentration}
            striped
            hoverable
          />
        ) : (
          <div className="py-12 text-center text-tertiary">
            {sectionMessage(
              outcome,
              'Loan type concentration data',
              'No loan balance is outstanding.',
              loanTypeRows,
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
